import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import UserProfileModal from './UserProfileModal';
import GroupParticipantsModal from './GroupParticipantsModal';

import { Message, Conversation, TypingIndicator, User } from '../types';

type SendMessageOptions = { replyTo?: string };

interface ChatAreaProps {
  conversation: Conversation | null;
  messages: Message[];
  currentUser: User;
  typingIndicators: TypingIndicator[];
  onSendMessage: (content: string, attachments?: File[], options?: SendMessageOptions) => boolean | Promise<boolean>;
  onEditMessage: (messageId: string, content: string) => void | Promise<void>;
  onDeleteMessageForMe: (messageId: string) => void | Promise<void>;
  onDeleteMessageForAll: (messageId: string) => void | Promise<void>;
  onReactToMessage: (messageId: string, emoji: string) => void;
  onPinMessage: (messageId: string) => void;
  isMobileView?: boolean;
  onBackToConversations?: () => void;
  onOpenContacts?: () => void;
  onDeleteConversation?: (conversationId: string) => void | Promise<void>;
}

const ChatArea = ({
  conversation,
  currentUser,
  messages,
  typingIndicators,
  onSendMessage,
  onEditMessage,
  onDeleteMessageForMe,
  onDeleteMessageForAll,
  onReactToMessage,
  onPinMessage,
  isMobileView = false,
  onBackToConversations,
  onOpenContacts,
  onDeleteConversation,
}: ChatAreaProps) => {
  const [messageInput, setMessageInput] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [deleteMenuMessageId, setDeleteMenuMessageId] = useState<string | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState<string | null>(null);
  const [showComposerEmojiPicker, setShowComposerEmojiPicker] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [replyToMessageId, setReplyToMessageId] = useState<string | null>(null);
  const [threadSearchQuery, setThreadSearchQuery] = useState('');
  const [showThreadSearch, setShowThreadSearch] = useState(false);
  const [profileUserId, setProfileUserId] = useState<string | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const composerEmojiPickerRef = useRef<HTMLDivElement>(null);
  const messageEmojiPickerRef = useRef<HTMLDivElement>(null);
  const deleteMenuRef = useRef<HTMLDivElement>(null);
  const headerMenuRef = useRef<HTMLDivElement>(null);

  const emojis = ['👍', '❤️', '😊', '😂', '🎉', '👏'];

  const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const highlightText = (text: string, query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return text;

    const regex = new RegExp(`(${escapeRegExp(trimmed)})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, idx) =>
      idx % 2 === 1 ? (
        <mark
          key={idx}
          className="rounded bg-yellow-200/60 px-0.5 text-inherit dark:bg-yellow-500/25"
        >
          {part}
        </mark>
      ) : (
        <React.Fragment key={idx}>{part}</React.Fragment>
      )
    );
  };

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;

    const onScroll = () => {
      const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottomRef.current = distanceToBottom < 120;
    };

    el.addEventListener('scroll', onScroll, { passive: true } as any);
    onScroll();
    return () => el.removeEventListener('scroll', onScroll as any);
  }, []);

  useEffect(() => {
    if (threadSearchQuery.trim()) return;
    if (!stickToBottomRef.current) return;
    const el = messagesContainerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [messages, threadSearchQuery]);

  useEffect(() => {
    setEditingMessageId(null);
    setEditingContent('');
    setDeleteMenuMessageId(null);
    setShowEmojiPicker(null);
    setHeaderMenuOpen(false);
  }, [conversation?.id]);

  useEffect(() => {
    if (!showThreadSearch) {
      setThreadSearchQuery('');
    }
  }, [showThreadSearch]);

  useEffect(() => {
    const onDocumentMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;

      if (showComposerEmojiPicker && composerEmojiPickerRef.current?.contains(target)) return;
      if (showEmojiPicker && messageEmojiPickerRef.current?.contains(target)) return;
      if (deleteMenuMessageId && deleteMenuRef.current?.contains(target)) return;
      if (headerMenuOpen && headerMenuRef.current?.contains(target)) return;

      setShowComposerEmojiPicker(false);
      setShowEmojiPicker(null);
      setDeleteMenuMessageId(null);
      setHeaderMenuOpen(false);
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [showComposerEmojiPicker, showEmojiPicker, deleteMenuMessageId, headerMenuOpen]);

  const handleSendMessage = async () => {
    if (messageInput.trim() || selectedFiles.length > 0) {
      const ok = await Promise.resolve(
        onSendMessage(
        messageInput,
        selectedFiles,
        replyToMessageId ? { replyTo: replyToMessageId } : undefined
        )
      );
      if (ok) {
        setMessageInput('');
        setSelectedFiles([]);
        setReplyToMessageId(null);
        setShowComposerEmojiPicker(false);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowComposerEmojiPicker(false);
      setShowEmojiPicker(null);
      setReplyToMessageId(null);
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSendMessage();
    }
  };

  const insertEmoji = (emoji: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setMessageInput((prev) => prev + emoji);
      return;
    }

    const start = textarea.selectionStart ?? messageInput.length;
    const end = textarea.selectionEnd ?? messageInput.length;
    const nextValue = messageInput.slice(0, start) + emoji + messageInput.slice(end);
    setMessageInput(nextValue);

    requestAnimationFrame(() => {
      textarea.focus();
      const cursor = start + emoji.length;
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getMessageTime = (date: Date) => {
    return date.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
  };

  const getMessageDate = (date: Date) => {
    const today = new Date();
    const messageDate = new Date(date);

    if (messageDate.toDateString() === today.toDateString()) {
      return 'Azi';
    }

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (messageDate.toDateString() === yesterday.toDateString()) {
      return 'Ieri';
    }

    return messageDate.toLocaleDateString('ro-RO', { month: 'short', day: 'numeric' });
  };

  const groupReactions = (reactions: Message['reactions']) => {
    const grouped = new Map<string, number>();
    for (const reaction of reactions ?? []) {
      grouped.set(reaction.emoji, (grouped.get(reaction.emoji) ?? 0) + 1);
    }
    return Array.from(grouped.entries()).map(([emoji, count]) => ({ emoji, count }));
  };

  if (!conversation) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-background">
        <Icon name="MessageSquare" size={64} className="text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Selectează o conversație pentru a începe
        </h3>
        <p className="text-sm text-muted-foreground text-center max-w-md">
          Alege o conversație din listă sau pornește una nouă pentru a comunica cu profesori, elevi și părinți.
        </p>
      </div>
    );
  }

  const currentTyping = typingIndicators.filter(
    (t) => t.conversationId === conversation.id && t.userId !== currentUser.id
  );

  const openProfileForUser = (userId: string) => {
    setProfileUserId(userId);
    setProfileOpen(true);
  };

  const onHeaderAvatarClick = () => {
    if (conversation.type === 'private') {
      const other = conversation.participants[0];
      if (!other) return;
      openProfileForUser(other.id);
      return;
    }
    setParticipantsOpen(true);
  };

  const getParentChildrenLabel = () => {
    if (conversation.type !== 'private') return null;
    const other = conversation.participants[0];
    if (other?.role !== 'parent') return null;
    const children = other.children ?? [];
    if (children.length === 0) return null;
    return children
      .map((c) => `${c.name}${c.classroomName ? ` (${c.classroomName})` : ''}`)
      .join(', ');
  };

  const parentChildrenLabel = getParentChildrenLabel();

  const visibleMessages = threadSearchQuery.trim()
    ? messages.filter((m) => {
        const q = threadSearchQuery.trim().toLowerCase();
        const contentMatch = m.content.toLowerCase().includes(q);
        const attachmentMatch = (m.attachments ?? []).some((a) => a.name.toLowerCase().includes(q));
        return contentMatch || attachmentMatch;
      })
    : messages;

  const replyToMessage = replyToMessageId
    ? messages.find((m) => m.id === replyToMessageId) ?? null
    : null;

  return (
    <div className="flex flex-col min-h-0 min-w-0 h-full bg-background">
      <UserProfileModal
        userId={profileUserId}
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        title="Profil contact"
      />
      <GroupParticipantsModal
        open={participantsOpen}
        onClose={() => setParticipantsOpen(false)}
        conversation={conversation}
        onOpenProfile={(id) => openProfileForUser(id)}
      />
      <div className="relative flex items-center justify-between p-4 border-b border-border bg-card">
        <div className="flex items-center gap-3 min-w-0">
          {isMobileView && onBackToConversations ? (
            <button
              type="button"
              onClick={onBackToConversations}
              className="p-2 -ml-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150"
              aria-label="Înapoi la convorbiri"
            >
              <Icon name="ChevronLeft" size={20} />
            </button>
          ) : null}
          <button
            type="button"
            onClick={onHeaderAvatarClick}
            className="rounded-full focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label={conversation.type === 'group' ? 'Vezi participanții' : 'Vezi profilul'}
          >
            {conversation.avatar ? (
              <Image
                src={conversation.avatar}
                alt={`${conversation.name} avatar`}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                {conversation.name.charAt(0).toUpperCase()}
              </div>
            )}
          </button>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-foreground truncate">{conversation.name}</h2>
            {conversation.type === 'group' ? (
              <p className="text-xs text-muted-foreground">
                {conversation.participants.length} participanți
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {parentChildrenLabel
                  ? `Părinte: ${parentChildrenLabel} • ${
                      conversation.participants[0]?.isOnline ? 'Online' : 'Offline'
                    }`
                  : conversation.participants[0]?.isOnline
                    ? 'Online'
                    : 'Offline'}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isMobileView && onOpenContacts ? (
            <button
              onClick={onOpenContacts}
              className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150"
              aria-label="Contacte"
            >
              <Icon name="Users" size={20} />
            </button>
          ) : null}
          <button
            onClick={() => setShowThreadSearch((prev) => !prev)}
            className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150"
            aria-label="Caută în conversație"
          >
            <Icon name="Search" size={20} />
          </button>
          <button
            onClick={() => setHeaderMenuOpen((p) => !p)}
            className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150"
            aria-label="Mai multe opțiuni"
            aria-expanded={headerMenuOpen}
          >
            <Icon name="MoreVertical" size={20} />
          </button>
          {headerMenuOpen ? (
            <div
              ref={headerMenuRef}
              className="absolute right-4 top-[calc(100%-0.5rem)] mt-2 w-56 rounded-md border border-border bg-card shadow-lg z-50 overflow-hidden"
              role="menu"
              aria-label="Meniu conversație"
            >
              {isMobileView && onBackToConversations ? (
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted flex items-center gap-2"
                  onClick={() => {
                    setHeaderMenuOpen(false);
                    onBackToConversations();
                  }}
                  role="menuitem"
                >
                  <Icon name="MessageSquare" size={16} className="text-muted-foreground" />
                  <span>Convorbiri</span>
                </button>
              ) : null}

              {onOpenContacts ? (
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted flex items-center gap-2"
                  onClick={() => {
                    setHeaderMenuOpen(false);
                    onOpenContacts();
                  }}
                  role="menuitem"
                >
                  <Icon name="Users" size={16} className="text-muted-foreground" />
                  <span>Contacte</span>
                </button>
              ) : null}

              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted flex items-center gap-2"
                onClick={() => {
                  setHeaderMenuOpen(false);
                  onHeaderAvatarClick();
                }}
                role="menuitem"
              >
                <Icon name={conversation.type === 'group' ? 'Users' : 'User'} size={16} className="text-muted-foreground" />
                <span>{conversation.type === 'group' ? 'Participanți' : 'Profil contact'}</span>
              </button>

              {onDeleteConversation ? (
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm text-error hover:bg-muted flex items-center gap-2"
                  onClick={() => {
                    setHeaderMenuOpen(false);
                    const ok = window.confirm('Ștergi această conversație din lista ta?');
                    if (!ok) return;
                    void Promise.resolve(onDeleteConversation(conversation.id));
                  }}
                  role="menuitem"
                >
                  <Icon name="Trash2" size={16} className="text-error" />
                  <span>Șterge conversația</span>
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {showThreadSearch && (
        <div className="px-4 py-3 border-b border-border bg-card">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Icon
                name="Search"
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                value={threadSearchQuery}
                onChange={(e) => setThreadSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setShowThreadSearch(false);
                }}
                placeholder="Caută în mesaje..."
                className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <button
              onClick={() => setShowThreadSearch(false)}
              className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150"
              aria-label="Închide căutarea"
            >
              <Icon name="X" size={18} />
            </button>
          </div>
          {threadSearchQuery.trim() && (
            <p className="mt-2 text-xs text-muted-foreground">
              {visibleMessages.length === 0
                ? 'Nu s-a găsit niciun mesaj.'
                : `${visibleMessages.length} rezultate`}
            </p>
          )}
        </div>
      )}

      <div
        ref={messagesContainerRef}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-4"
      >
        {visibleMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-12">
            <Icon name="MessageSquare" size={56} className="text-muted-foreground mb-4" />
            <h3 className="text-base font-semibold text-foreground mb-1">
              {threadSearchQuery.trim() ? 'Nu am găsit mesaje' : 'Încă nu există mesaje'}
            </h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              {threadSearchQuery.trim()
                ? 'Încearcă un alt cuvânt cheie.'
                : 'Trimite primul mesaj ca să pornești conversația.'}
            </p>
          </div>
        ) : (
          visibleMessages.map((message, index) => {
            const showDateSeparator =
              index === 0 ||
              getMessageDate(visibleMessages[index - 1].timestamp) !==
                getMessageDate(message.timestamp);
            const isCurrentUser = message.senderId === currentUser.id;
            const replyTarget = message.replyTo
              ? messages.find((m) => m.id === message.replyTo) ?? null
              : null;
            const groupedReactions = groupReactions(message.reactions);

            return (
              <React.Fragment key={message.id}>
                {showDateSeparator && (
                  <div className="flex items-center justify-center my-4">
                    <div className="px-3 py-1 bg-muted rounded-full">
                      <span className="text-xs font-medium text-muted-foreground">
                        {getMessageDate(message.timestamp)}
                      </span>
                    </div>
                  </div>
                )}

                <div className={`flex gap-3 ${isCurrentUser ? 'flex-row-reverse' : ''}`}>
                  <div className="flex-shrink-0">
                    {(() => {
                      const avatarUrl = isCurrentUser ? currentUser.avatar : message.senderAvatar;
                      const avatarName = isCurrentUser ? currentUser.name : message.senderName;
                      if (avatarUrl) {
                        return (
                          <Image
                            src={avatarUrl}
                            alt={`${avatarName} avatar`}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        );
                      }
                      return (
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                          {avatarName.charAt(0).toUpperCase()}
                        </div>
                      );
                    })()}
                  </div>

                  <div className={`flex-1 max-w-[70%] ${isCurrentUser ? 'flex justify-end' : ''}`}>
                    <div>
                      {!isCurrentUser && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-foreground">
                            {message.senderName}
                          </span>
                          <span className="text-xs text-muted-foreground capitalize">
                            {message.senderRole}
                          </span>
                        </div>
                      )}

                      <div
                        className={`relative group rounded-lg p-3 ${
                          isCurrentUser
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-card border border-border'
                        }`}
                      >
                        {message.isPinned && (
                          <div className="flex items-center gap-1 mb-2 pb-2 border-b border-current/20">
                            <Icon name="Pin" size={12} className="opacity-70" />
                            <span className="text-xs opacity-70">Mesaj fixat</span>
                          </div>
                        )}

                        {replyTarget && (
                          <div
                            className={`mb-2 rounded-md border border-current/20 px-2 py-1 text-xs ${
                              isCurrentUser ? 'bg-primary-foreground/10' : 'bg-muted'
                            }`}
                          >
                            <span className="font-medium">
                              Răspuns către {replyTarget.senderName}
                            </span>
                            <span className="opacity-70">{': '}</span>
                            <span className="opacity-70 line-clamp-1">{replyTarget.content}</span>
                          </div>
                        )}

                        {editingMessageId === message.id ? (
                          <div className="space-y-2">
                            <textarea
                              value={editingContent}
                              onChange={(e) => setEditingContent(e.target.value)}
                              className="w-full min-h-[64px] rounded-md border border-current/25 bg-background/80 px-3 py-2 text-sm text-foreground"
                            />
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingMessageId(null);
                                  setEditingContent('');
                                }}
                              >
                                Renunță
                              </Button>
                              <Button
                                size="sm"
                                onClick={async () => {
                                  const trimmed = editingContent.trim();
                                  if (!trimmed) return;
                                  await onEditMessage(message.id, trimmed);
                                  setEditingMessageId(null);
                                  setEditingContent('');
                                }}
                              >
                                Salvează
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap break-words">
                            {message.isDeleted ? (
                              <em className="opacity-80">Mesaj șters</em>
                            ) : (
                              highlightText(message.content, threadSearchQuery)
                            )}
                          </p>
                        )}

                      {message.attachments && message.attachments.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {message.attachments.map((attachment) => (
                            <div
                              key={attachment.id}
                              className={`flex items-center gap-2 p-2 rounded ${
                                isCurrentUser ? 'bg-primary-foreground/10' : 'bg-muted'
                              }`}
                            >
                              <Icon name="File" size={16} />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium truncate">{attachment.name}</p>
                                <p className="text-xs opacity-70">
                                  {formatFileSize(attachment.size)}
                                </p>
                              </div>
                              <a
                                href={attachment.url}
                                download={attachment.name}
                                className="p-1 rounded hover:bg-muted-foreground/10"
                                aria-label={`Descarcă ${attachment.name}`}
                              >
                                <Icon name="Download" size={14} />
                              </a>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between gap-2 mt-2">
                        <span className="text-xs opacity-70">
                          {getMessageTime(message.timestamp)}
                          {message.editedAt && !message.isDeleted ? ' • editat' : ''}
                        </span>
                        {isCurrentUser && (
                          <Icon
                            name={message.isRead ? 'CheckCheck' : 'Check'}
                            size={14}
                            className="opacity-70"
                          />
                        )}
                      </div>

                      {groupedReactions.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {groupedReactions.map((reaction) => (
                            <div
                              key={reaction.emoji}
                              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                                isCurrentUser ? 'bg-primary-foreground/10' : 'bg-muted'
                              }`}
                            >
                              <span>{reaction.emoji}</span>
                              <span className="opacity-70">{reaction.count}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div
                        className={`absolute ${
                          isCurrentUser ? 'right-2' : 'left-2'
                        } -top-3 z-10 hidden group-hover:flex items-center gap-1 p-1 bg-background/95 text-foreground border border-border rounded-md shadow-lg backdrop-blur`}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setShowEmojiPicker((prev) => (prev === message.id ? null : message.id))
                          }
                          className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
                          aria-label="Reacționează la mesaj"
                        >
                          <Icon name="Smile" size={14} className="text-foreground" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setReplyToMessageId(message.id);
                            setShowComposerEmojiPicker(false);
                            requestAnimationFrame(() => textareaRef.current?.focus());
                          }}
                          className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
                          aria-label="Răspunde la mesaj"
                        >
                          <Icon name="Reply" size={14} className="text-foreground" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onPinMessage(message.id)}
                          className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
                          aria-label="Fixează mesajul"
                        >
                          <Icon name="Pin" size={14} className="text-foreground" />
                        </button>
                        {isCurrentUser && !message.isDeleted && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingMessageId(message.id);
                              setEditingContent(message.content);
                              setDeleteMenuMessageId(null);
                              setShowEmojiPicker(null);
                            }}
                            className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
                            aria-label="Editează mesajul"
                          >
                            <Icon name="Pencil" size={14} className="text-foreground" />
                          </button>
                        )}
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => {
                              setDeleteMenuMessageId((prev) => (prev === message.id ? null : message.id));
                              setShowEmojiPicker(null);
                            }}
                            className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
                            aria-label="Șterge mesajul"
                          >
                            <Icon name="Trash2" size={14} className="text-foreground" />
                          </button>

                          {deleteMenuMessageId === message.id && (
                            <div
                              ref={deleteMenuRef}
                              className="absolute right-0 top-full mt-1 w-56 rounded-md border border-border bg-card shadow-lg z-50 overflow-hidden"
                            >
                              <button
                                type="button"
                                className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                                onClick={async () => {
                                  setDeleteMenuMessageId(null);
                                  if (!window.confirm('Ștergi mesajul doar pentru tine?')) return;
                                  await onDeleteMessageForMe(message.id);
                                }}
                              >
                                Șterge pentru mine
                              </button>
                              {isCurrentUser && !message.isDeleted && (
                                <button
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-sm text-destructive hover:bg-muted"
                                  onClick={async () => {
                                    setDeleteMenuMessageId(null);
                                    if (!window.confirm('Ștergi mesajul pentru toți participanții?')) return;
                                    await onDeleteMessageForAll(message.id);
                                  }}
                                >
                                  Șterge pentru toți
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {showEmojiPicker === message.id && (
                        <div
                          ref={messageEmojiPickerRef}
                          className={`absolute bottom-full mb-2 ${
                            isCurrentUser ? 'right-0' : 'left-0'
                          } flex gap-1 p-2 bg-background text-foreground border border-border rounded-md shadow-lg z-10`}
                        >
                          {emojis.map((emoji) => (
                            <button
                              key={emoji}
                              onClick={() => {
                                onReactToMessage(message.id, emoji);
                                setShowEmojiPicker(null);
                              }}
                              className="p-2 rounded hover:bg-muted text-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        }))}

        {currentTyping.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce [animation-delay:0.2s]" />
              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce [animation-delay:0.4s]" />
            </div>
            <span>
              {currentTyping.map((t) => t.userName).join(', ')}{' '}
              tastează...
            </span>
          </div>
        )}

      </div>

      <div className="relative p-4 border-t border-border bg-card">
        {replyToMessage && (
          <div className="mb-3 flex items-center justify-between gap-2 rounded-md border border-border bg-muted px-3 py-2">
            <div className="min-w-0">
              <p className="text-xs font-medium text-foreground">
                Răspunzi la {replyToMessage.senderName}
              </p>
              <p className="text-xs text-muted-foreground truncate">{replyToMessage.content}</p>
            </div>
            <button
              onClick={() => setReplyToMessageId(null)}
              className="p-1 rounded hover:bg-background"
              aria-label="Anulează răspunsul"
            >
              <Icon name="X" size={14} />
            </button>
          </div>
        )}

        {selectedFiles.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-3 py-2 bg-muted rounded-md"
              >
                <Icon name="File" size={16} className="text-muted-foreground" />
                <span className="text-sm text-foreground">{file.name}</span>
                <button
                  onClick={() => removeFile(index)}
                  className="p-1 rounded hover:bg-background"
                  aria-label="Elimină fișierul"
                >
                  <Icon name="X" size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-end gap-2 min-w-0">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150"
            aria-label="Atașează fișier"
          >
            <Icon name="Paperclip" size={20} />
          </button>

          <div className="flex-1 min-w-0">
            <textarea
              ref={textareaRef}
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Scrie un mesaj..."
              rows={1}
              className="w-full min-w-0 px-4 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          <button
            onClick={() => setShowComposerEmojiPicker((prev) => !prev)}
            className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150"
            aria-label="Adaugă emoji"
          >
            <Icon name="Smile" size={20} />
          </button>

          {showComposerEmojiPicker && (
            <div
              ref={composerEmojiPickerRef}
              className="absolute bottom-[76px] right-4 flex gap-1 p-2 bg-card border border-border rounded-md shadow-lg z-10"
            >
              {emojis.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => {
                    insertEmoji(emoji);
                    setShowComposerEmojiPicker(false);
                  }}
                  className="p-2 rounded hover:bg-muted text-lg"
                  aria-label={`Inserează ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}

          <Button
            onClick={handleSendMessage}
            disabled={!messageInput.trim() && selectedFiles.length === 0}
            iconName="Send"
            size="default"
          >
            Trimite
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatArea;
